import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Map, ArrowLeft, FileText, Target, Workflow } from 'lucide-react';
export function UXDocumentation() {
  const sections = [{
    title: 'User Personas',
    description: 'Detailed profiles of our key user types and their needs',
    icon: Users,
    path: '/ux/personas',
    color: 'from-pink-500 to-purple-500',
    bgColor: 'bg-pink-50',
    iconColor: 'text-pink-600'
  }, {
    title: 'User Journey Map',
    description: 'Visual representation of user flows and touchpoints',
    icon: Map,
    path: '/ux/journey',
    color: 'from-cyan-500 to-blue-500',
    bgColor: 'bg-cyan-50',
    iconColor: 'text-cyan-600'
  }, {
    title: 'Use Cases',
    description: 'Real-world scenarios and application patterns',
    icon: Target,
    path: '/ux/use-cases',
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-amber-50',
    iconColor: 'text-amber-600',
    comingSoon: true
  }, {
    title: 'User Flows',
    description: 'Step-by-step task completion pathways',
    icon: Workflow,
    path: '/ux/flows',
    color: 'from-emerald-500 to-green-500',
    bgColor: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    comingSoon: true
  }];
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50" data-id="element-3818">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-3819">
        {/* Header */}
        <div className="mb-12" data-id="element-3820">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors mb-6" data-id="element-3821">
            <ArrowLeft size={16} data-id="element-3822" />
            Back to Home
          </Link>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-3823">
            <div className="flex items-center gap-3 mb-4" data-id="element-3824">
              <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl shadow-lg" data-id="element-3825">
                <FileText size={32} className="text-white" data-id="element-3826" />
              </div>
              <div data-id="element-3827">
                <h1 className="text-4xl font-bold text-slate-900" data-id="element-3828">
                  UX Documentation
                </h1>
                <p className="text-lg text-slate-600 mt-1" data-id="element-3829">
                  User research, personas, and journey mapping
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3830">
          {sections.map((section, index) => <motion.div key={section.path} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-3831">
              <Link to={section.comingSoon ? '#' : section.path} className={`block h-full ${section.comingSoon ? 'pointer-events-none' : ''}`} data-id="element-3832">
                <div className={`relative h-full bg-white/60 backdrop-blur-xl border border-slate-200/50 rounded-2xl p-8 transition-all duration-300 ${section.comingSoon ? 'opacity-60' : 'hover:shadow-xl hover:border-slate-300/50 hover:-translate-y-1'}`} data-id="element-3833">
                  {section.comingSoon && <div className="absolute top-4 right-4" data-id="element-3834">
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full" data-id="element-3835">
                        Coming Soon
                      </span>
                    </div>}

                  <div className={`inline-flex p-4 ${section.bgColor} rounded-xl mb-6`} data-id="element-3836">
                    <section.icon size={32} className={section.iconColor} data-id="element-3837" />
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-3838">
                    {section.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed" data-id="element-3839">
                    {section.description}
                  </p>

                  {!section.comingSoon && <div className="mt-6 flex items-center gap-2 text-sm font-medium text-slate-900" data-id="element-3840">
                      <span data-id="element-3841">Explore</span>
                      <motion.div animate={{
                  x: [0, 4, 0]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-3842">
                        →
                      </motion.div>
                    </div>}
                </div>
              </Link>
            </motion.div>)}
        </div>

        {/* Footer Note */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="mt-12 p-6 bg-blue-50/50 backdrop-blur-xl border border-blue-100/50 rounded-xl" data-id="element-3843">
          <div className="flex items-start gap-3" data-id="element-3844">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-3845">
              <FileText size={20} className="text-blue-600" data-id="element-3846" />
            </div>
            <div data-id="element-3847">
              <h3 className="text-sm font-semibold text-blue-900 mb-1" data-id="element-3848">
                Living Documentation
              </h3>
              <p className="text-sm text-blue-700 leading-relaxed" data-id="element-3849">
                This UX documentation is continuously updated based on user
                research, feedback, and product evolution. Each section provides
                insights into how different user types interact with the
                platform.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}